/*
  Automatic cart gifts
  --------------------
  Keeps promotional gift lines in step with what is actually in the cart. Each
  rule says: when a trigger line is present, a gift line must exist; when the
  trigger goes away, the gift goes with it.

  Gifts are always added as ONE-TIME lines (no selling plan). For subscription
  promos that is what keeps renewals correct: Skio builds the contract from
  selling-plan lines only, so a one-time gift cannot recur.

  Only lines carrying a rule's own property are ever touched. A gift product
  that arrived some other way - a discount app, or the customer adding it
  themselves - is left alone.

  Public API: window.CartGifts.reconcile() -> Promise<boolean changed>
*/
(function () {
  var config = window.CART_GIFTS_CONFIG;
  if (!config || !config.rules || config.rules.length === 0) return;

  var rules = config.rules;
  // Marks our own cart writes so the fetch hook below does not react to them.
  var MARKER = 'X-Cart-Gifts';
  var RELOAD_GUARD_KEY = 'cart-gifts-reloaded';

  // Every cart write goes through this chain. Shopify's cart endpoints are not
  // safe to call in parallel: concurrent writes read stale state and undo each
  // other, which is exactly how duplicate gift lines appear.
  var queue = Promise.resolve();

  function log(message, detail) {
    if (config.debug) console.log('[cart-gifts] ' + message, detail === undefined ? '' : detail);
  }

  function isManaged(line) {
    if (!line.properties) return false;
    return rules.some(function (rule) {
      return !!line.properties[rule.property];
    });
  }

  function post(url, payload) {
    var headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
    headers[MARKER] = '1';

    return fetch(url, { method: 'POST', headers: headers, body: JSON.stringify(payload) });
  }

  function getCart() {
    var headers = { Accept: 'application/json' };
    headers[MARKER] = '1';

    return fetch('/cart.js', { headers: headers }).then(function (response) {
      return response.json();
    });
  }

  function slotKey(property, variantId) {
    return property + '|' + variantId;
  }

  // A discount code the visitor has applied to the session, via /discount/CODE.
  // A code typed at checkout never reaches the cart, so it cannot be seen here.
  // Deliberately ignores the "applicable" flag: a discount that gives an item
  // away is not applicable until that item is in the cart, which is exactly
  // what this rule is about to fix.
  function hasDiscountCode(cart, code) {
    if (!code || !cart.discount_codes) return false;

    var wanted = String(code).trim().toLowerCase();

    return cart.discount_codes.some(function (entry) {
      return String(entry && entry.code || '').trim().toLowerCase() === wanted;
    });
  }

  // What the cart should look like, and what it looks like now.
  function plan(cart) {
    var existing = {};
    var duplicates = [];

    // Index by distinct property, not by rule: several rules may share one
    // property, and indexing per rule would count a single line as a duplicate
    // of itself and delete it.
    var properties = rules.map(function (rule) {
      return rule.property;
    }).filter(function (property, index, all) {
      return all.indexOf(property) === index;
    });

    cart.items.forEach(function (line) {
      properties.forEach(function (property) {
        if (!line.properties || !line.properties[property]) return;

        var key = slotKey(property, line.variant_id);
        if (existing[key]) duplicates.push(line.key);
        else existing[key] = { key: line.key, quantity: line.quantity };
      });
    });

    var desired = {};

    rules.forEach(function (rule) {
      var maxQty = Math.max(0, parseInt(rule.maxQty, 10) || 0);
      if (maxQty === 0) return;

      // The code sits on the cart rather than on any line, so it gates the
      // whole rule before a single item is looked at.
      if (rule.requireDiscountCode && !hasDiscountCode(cart, rule.requireDiscountCode)) return;

      // A code rule with no line conditions is satisfied by the code alone:
      // there is no trigger line to find, and the cart may even be empty.
      if (rule.requireDiscountCode && !rule.triggerProductIds && !rule.requireSellingPlan) {
        if (!rule.giftVariantId) return;

        var codeKey = slotKey(rule.property, rule.giftVariantId);
        desired[codeKey] = Math.max(desired[codeKey] || 0, maxQty);
        return;
      }

      cart.items.forEach(function (line) {
        // A gift line must never trigger a gift, or rules could feed each other.
        if (isManaged(line)) return;
        if (rule.requireSellingPlan && !line.selling_plan_allocation) return;
        if (rule.triggerProductIds && rule.triggerProductIds.indexOf(line.product_id) === -1) return;

        // No giftVariantId means the gift is another unit of the trigger itself.
        var variantId = rule.giftVariantId || line.variant_id;
        var key = slotKey(rule.property, variantId);
        var qty = Math.min(line.quantity, maxQty);

        desired[key] = Math.max(desired[key] || 0, qty);
      });
    });

    var operations = [];

    duplicates.forEach(function (key) {
      operations.push({ type: 'change', key: key, quantity: 0 });
    });

    Object.keys(existing).forEach(function (key) {
      var want = desired[key] || 0;
      if (existing[key].quantity !== want) {
        operations.push({ type: 'change', key: existing[key].key, quantity: want });
      }
    });

    Object.keys(desired).forEach(function (key) {
      if (existing[key] || desired[key] <= 0) return;

      var parts = key.split('|');
      operations.push({
        type: 'add',
        property: parts[0],
        id: parseInt(parts[1], 10),
        quantity: desired[key]
      });
    });

    return operations;
  }

  function apply(operation) {
    if (operation.type === 'change') {
      return post('/cart/change.js', { id: operation.key, quantity: operation.quantity });
    }

    var properties = {};
    properties[operation.property] = 'true';

    // A sold-out gift must not block the purchase, so failures are swallowed:
    // the customer still checks out, just without the free item.
    return post('/cart/add.js', {
      id: operation.id,
      quantity: operation.quantity,
      properties: properties
    }).then(function (response) {
      if (!response.ok) log('gift could not be added, continuing without it', operation.id);
    });
  }

  function run(operations) {
    return operations.reduce(function (chain, operation) {
      return chain.then(function () {
        return apply(operation).catch(function (error) {
          log('operation failed', error);
        });
      });
    }, Promise.resolve());
  }

  // Identifies a set of cart writes, so the reload guard below can tell "the
  // same thing all over again" from "something new happened".
  function signature(operations) {
    return operations.map(function (operation) {
      return operation.type + ':' + (operation.key || operation.id) + ':' + operation.quantity;
    }).sort().join('|');
  }

  // Resolves to the signature of the writes performed, or null if none were.
  function reconcile() {
    return getCart().then(function (cart) {
      var operations = plan(cart);
      log('operations', operations);

      if (operations.length === 0) return null;

      var stamp = signature(operations);
      return run(operations).then(function () {
        return stamp;
      });
    }).catch(function (error) {
      log('reconcile failed', error);
      return null;
    });
  }

  /* ----- overlay ----------------------------------------------------------
     Reconciling means several round trips to Shopify and, on the cart page, a
     re-render afterwards. Without a cover the customer watches line items and
     totals shift under the cursor. Liquid renders the overlay already visible
     when it knows a reconcile is pending, so there is no flash either way. */

  function overlay() {
    return document.querySelector('[data-cart-gifts-overlay]');
  }

  function showOverlay() {
    var element = overlay();
    if (element) element.classList.remove('is-hidden');
  }

  function hideOverlay() {
    var element = overlay();
    if (element) element.classList.add('is-hidden');
  }

  function onCartPage() {
    return window.location.pathname.replace(/\/$/, '').endsWith('/cart');
  }

  function lastReloadAttempt() {
    try { return sessionStorage.getItem(RELOAD_GUARD_KEY); } catch (error) { return null; }
  }

  function rememberReloadAttempt(stamp) {
    try { sessionStorage.setItem(RELOAD_GUARD_KEY, stamp); } catch (error) { /* private mode */ }
  }

  function forgetReloadAttempt() {
    try { sessionStorage.removeItem(RELOAD_GUARD_KEY); } catch (error) { /* private mode */ }
  }

  function reconcileAndRefresh() {
    showOverlay();

    queue = queue.then(reconcile, reconcile).then(function (stamp) {
      if (!stamp) {
        forgetReloadAttempt();
        hideOverlay();
        return false;
      }

      if (!onCartPage()) {
        hideOverlay();
        return true;
      }

      // Loop protection, keyed on what was actually written rather than on a
      // flag saying "we have reloaded before". A sticky flag gets stranded the
      // moment a page navigates away before the settling reconcile runs, and
      // from then on every add is left showing a stale cart. Comparing
      // signatures means only a genuine repeat - the same writes again right
      // after a reload, so reloading cannot help - stops the reload.
      if (lastReloadAttempt() === stamp) {
        log('skipping reload, the previous one did not settle the cart');
        forgetReloadAttempt();
        hideOverlay();
        return true;
      }

      rememberReloadAttempt(stamp);
      // Overlay stays up through the reload so the stale cart is never shown.
      window.location.reload();
      return true;
    });

    return queue;
  }

  window.CartGifts = { reconcile: reconcileAndRefresh };

  // React to cart writes made by the rest of the theme (product forms, quantity
  // controls, apps). Requests we made ourselves carry the marker header.
  var debounce = null;
  var nativeFetch = window.fetch;

  function isOurs(input, init) {
    var headers = (init && init.headers) || (input && input.headers);
    if (!headers) return false;
    if (typeof headers.get === 'function') return headers.get(MARKER) !== null;
    return Object.keys(headers).some(function (name) {
      return name.toLowerCase() === MARKER.toLowerCase();
    });
  }

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isCartWrite = /\/cart\/(add|change|update|clear)(\.js)?/.test(url);
    var ours = isOurs(input, init);

    return nativeFetch.apply(this, arguments).then(function (response) {
      if (isCartWrite && !ours && response.ok) {
        clearTimeout(debounce);
        debounce = setTimeout(reconcileAndRefresh, 200);
      }
      return response;
    });
  };

  if (config.autoReconcile) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', reconcileAndRefresh);
    } else {
      reconcileAndRefresh();
    }
  } else {
    hideOverlay();
  }
})();
